/**
 * A modal window is a graphical control element subordinate to an application&#x27;s main window. It creates a mode that disables the main window but keeps it visible, with the dialog window as a child window in front of it
 *
 * @module views/Layouts/OverlayModal
 */
import './OverlayModal.scss';

import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { FocusScope } from '@react-aria/focus';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Modal as PalModal } from 'web-palette/dist/components/Modal/Modal';
import { STYLES as PALETTE_STYLES } from 'web-palette/dist/util/constants/styles';

import Button from '@ulta/core/components/Button/Button';
import UseKeyPress from '@ulta/core/hooks/useKeyPress/UseKeyPress';
import { ARIA_LIVE_LOCATION, AriaLiveRegion } from '@ulta/core/providers/AriaLiveRegionProvider/AriaLiveRegionProvider';
import { useOverlay } from '@ulta/core/providers/OverlayProvider/OverlayProvider';
import constants from '@ulta/core/utils/constants/constants';
import { isServer } from '@ulta/core/utils/device_detection/device_detection';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { isFunction } from '@ulta/core/utils/types/types';

import * as utils from './OverlayModal';

/**
* Represents a Overlay component
* @type {React.FC<OverlayModalProps>}
*/
const OverlayModal = forwardRef( ( props, ref ) => {
  const {
    ariaLabel,
    ariaLabelledby,
    children,
    closeAccessibilityLabel = defaultProps.closeAccessibilityLabel,
    customClassName,
    disableClose,
    isVideoModal
  } = props;
  const { closeOverlay } = useOverlay();

  const [isOpen, buttonRef] = useCustomOverlay( props, ref );

  if( isServer() ){
    return null;
  }

  const showCloseButton = !disableClose;

  const formattedCloseActionElement = ( showCloseButton &&
    <>
      <AriaLiveRegion location={ ARIA_LIVE_LOCATION.Overlay } />
      <Button
        variant='unstyled'
        className={ PALETTE_STYLES.OVERLAY.HEADER_CLOSE_BUTTON }
        ariaLabel={ closeAccessibilityLabel }
        onClick={ () => closeOverlay( { userCancelled: true } ) }
        ref={ buttonRef }
        iconImage='X'
        iconSize='xl'
        iconOnly={ true }
        ariaHiddenIcon={ true }
      />
    </>
  );

  const component =  (
    <>
      <div className='OverlayModal'>
        <FocusScope contain={ true }>
          <PalModal
            externalCloseActionElement={ formattedCloseActionElement }
            panelRef={ ref }
            ariaLabel={ ariaLabel }
            ariaLabelledBy={ ariaLabelledby }
            isOpen={ isOpen }
            className={ classNames(
              { 'OverlayModal--modalVideo': isVideoModal,
                ...( customClassName && { [ `${OVERLAY_CLASS}--wrapper--${customClassName}` ]: customClassName } )
              }
            ) }
          >
            { children }
          </PalModal>
        </FocusScope>
      </div>
    </>
  );

  return createPortal( component, document.body );
} );

/**
 * Manages state for a custom Overlay and polyfills the div to mimic an HTML dialog
 * @param {object} props - Component props
 * @param {object} ref - Flyout ref
 * @returns {array} Returns array of [isOpen, buttonRef]
 */
export const useCustomOverlay = ( props, ref = {} ) => {
  const { disableClose, onClose } = props;
  const [isOpen, setIsOpen] = useState( false );
  const buttonRef = useRef();

  useEffect( () => {
    // Allow time for DOM to update so the initial element can render off screen, then
    // when we flip this to true it will animate in
    const timeOut = setTimeout( () => {
      setIsOpen( true );
    }, 0 );

    return () => clearTimeout( timeOut );
  }, [] );

  const onOverlayClose = useCallback( ( e, data ) => {
    return utils.composeOnOverlayClose( { disableClose, e, data }, { onClose, setIsOpen } );
  }, [onClose, setIsOpen, disableClose] );

  useEffect( () => {
    return utils.initializeReferenceClose( { ref }, { onOverlayClose } );
  }, [onOverlayClose, disableClose] );

  // Handle ESC key
  UseKeyPress( constants.ESC_KEY, { current: document }, ( e ) => {
    return utils.composeOnUserKeyPress( { disableClose, e }, { onOverlayClose } );
  } );

  useEffect( () => {
    utils.focusCloseButtonOnOpen( { isOpen, buttonRef } );
  }, [isOpen, buttonRef] );

  return [isOpen, buttonRef];
};

/**
 * @method focusCloseButtonOnOpen
 * @summary Focuses the close button when the overlay opens (for keyboard users).
 * @param {object} data - Object which contains isOpen and buttonRef.
 */
export const focusCloseButtonOnOpen = ( data ) => {
  const { isOpen, buttonRef } = data;
  if( !isOpen ){
    return;
  }
  buttonRef.current?.focus();
};

/**
 * @method initializeReferenceClose
 * @summary function initializes the close function of the reference object
 * @param {object} data - Object which contains data.
 * @param {object} methods - an object containing methods to be passed into the function.
 */
export const initializeReferenceClose = ( data, methods ) => {
  const { ref } = data;
  const { onOverlayClose } = methods;
  if( !ref?.current ){
    return;
  }

  // Polyfill the .close function on the element
  // we only set this once because if overlay content gets replaced we will have lost the og reference
  if( !isFunction( ref?.current?.close ) ){

    ref.current.close = onOverlayClose;
  }
};

/**
 * @method composeOnOverlayClose
 * @summary function sets the open and closng state
 * @param {object} overlayData - Object which contains data.
 * @param {object} methods - an object containing methods to be passed into the function.
 */
export const composeOnOverlayClose = ( overlayData, methods ) => {
  const { disableClose, e, data } = overlayData;
  const { onClose, setIsOpen } = methods;
  if( disableClose ){
    return;
  }
  onClose( e, data );
  setIsOpen( false );
};

/**
 * @method composeOnUserKeyPress
 * @summary function closes the overlay on escape key press
 * @param {object} data - Object which contains data.
 * @param {object} methods - an object containing methods to be passed into the function.
 */
export const composeOnUserKeyPress = ( data, methods ) => {
  const { disableClose, e } = data;
  const { onOverlayClose } = methods;
  if( disableClose ){
    return;
  }
  return onOverlayClose( e, { userCancelled: true } );
};

/**
* Overlay class
* @constant {string}
*/
export const OVERLAY_CLASS = 'OverlayModal';


/**
* Default values for passed properties
* @type {object}
* @property {string} closeAccessibilityLabel - Sets close accessibility label
*/

// TODO: remove default label value when content model is update.
export const defaultProps = {
  closeAccessibilityLabel: 'Close'
};

/**
  * Property type definitions
  * @typedef OverlayModalProps
  * @type {object}
  * @property {string} ariaLabel - Sets aria label in the modal
  * @property {string} ariaLabelledby - Sets aria labelled by in the modal
  * @property {string} closeAccessibilityLabel - Sets close accessibility label
  * @property {string} customClassName - Custom class applied to modal container
  * @property {boolean} disableClose - Disables modal close via ESC, backdrop, or close button
  * @property {object} footerActions - Sets the footer action buttons or elements for the modal
  * @property {boolean} isOpen - Indicates whether modal is open
  * @property {function} onClose - Handles the modal close event
  * @property {object} subtitle - Sets subtitle for modal
  * @property {string} supportText - Sets the support text for modal
  * @property {object} title - Sets modal title and optional HTML tag
  * @property {boolean} isVideoModal - Enables video modal styling
  * @property {React.ReactNode} children - Modal body content
  */

/**
* @type {OverlayModalProps}
*/
export const propTypes = {
  ariaLabel: PropTypes.string,
  ariaLabelledby: PropTypes.string,
  closeAccessibilityLabel: PropTypes.string,
  customClassName: PropTypes.string,
  disableClose: PropTypes.bool,
  footerActions: PropTypes.object,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  subtitle: PropTypes.object,
  supportText: PropTypes.string,
  title: PropTypes.object,
  isVideoModal: PropTypes.bool,
  children: PropTypes.node
};

OverlayModal.propTypes = propTypes;
OverlayModal.defaultProps = defaultProps;
OverlayModal.displayName = 'Overlay';

export default OverlayModal;
